// ChoroplethMap — วาดแผนที่ SVG จาก TopoJSON ด้วย d3-geo
// projection = geoMercator().fitSize() ต่อ view (รองรับ drill-down เข้าจังหวัด)
import { geoMercator, geoPath, type GeoProjection } from "d3-geo";
import { scaleThreshold, scaleQuantile } from "d3-scale";
import { feature, merge } from "topojson-client";

const SVG_NS = "http://www.w3.org/2000/svg";

// อ่านสเกลสี map-seq-0..5 จาก CSS custom properties (theme-aware)
export function seqColors(): string[] {
  const cs = getComputedStyle(document.documentElement);
  const out: string[] = [];
  for (let i = 0; i <= 5; i++) {
    const v = cs.getPropertyValue(`--map-seq-${i}`).trim();
    if (v) out.push(v);
  }
  return out.length ? out : ["#c9ead6", "#6cc495", "#2c7f54"];
}

function cssVar(name: string, fallback: string): string {
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v || fallback;
}

export interface RenderOptions {
  values: Record<string, number>;
  bins?: number[]; // เส้นแบ่งชั้นจาก server (ให้ legend คงที่)
  onSelect?: (areaCode: string) => void;
  selected?: string | null;
  filterParent?: string; // แสดงเฉพาะพื้นที่ที่ parent_code ตรงกับค่านี้ (drill-down)
  formatValue?: (v: number) => string;
  backgroundImage?: (areaCode: string) => string | undefined;
  backgroundImageOpacity?: number;
  showAreaLabels?: boolean;
  landingAmphoe?: boolean;
  regionalBackground?: boolean;
  reliefEdges?: boolean;
  showOuterBoundary?: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Topo = any;

export class ChoroplethMap {
  private svg: SVGSVGElement;
  private tooltip: HTMLDivElement;
  private contentLayer: SVGGElement | null = null;
  private width = 0;
  private height = 0;
  private zoom = 1;
  private translateX = 0;
  private translateY = 0;
  private dragging = false;
  private wasDragged = false;
  private dragStartX = 0;
  private dragStartY = 0;
  private dragTranslateX = 0;
  private dragTranslateY = 0;

  constructor(private container: HTMLElement) {
    this.svg = document.createElementNS(SVG_NS, "svg");
    this.svg.classList.add("choropleth");
    this.svg.setAttribute("role", "group");
    this.svg.setAttribute("aria-label", "แผนที่ประเทศไทย");
    this.container.appendChild(this.svg);

    this.tooltip = document.createElement("div");
    this.tooltip.className = "map-tooltip";
    this.tooltip.style.display = "none";
    this.container.appendChild(this.tooltip);

    const controls = document.createElement("div");
    controls.className = "map-zoom-controls";
    controls.setAttribute("aria-label", "ควบคุมการซูมแผนที่");
    controls.innerHTML = `
      <button type="button" class="map-zoom-button" data-zoom="in" aria-label="ซูมเข้า" title="ซูมเข้า">+</button>
      <button type="button" class="map-zoom-button" data-zoom="out" aria-label="ซูมออก" title="ซูมออก">−</button>
      <button type="button" class="map-zoom-button map-zoom-reset" data-zoom="reset" aria-label="กลับขนาดปกติ" title="กลับขนาดปกติ">↺</button>
    `;
    controls.addEventListener("click", (event) => {
      const button = (event.target as HTMLElement).closest<HTMLButtonElement>("[data-zoom]");
      if (!button) return;
      const action = button.dataset.zoom;
      if (action === "in") this.zoomAt(this.width / 2, this.height / 2, 1.35);
      if (action === "out") this.zoomAt(this.width / 2, this.height / 2, 1 / 1.35);
      if (action === "reset") this.resetZoom();
    });
    this.container.appendChild(controls);

    this.svg.addEventListener(
      "wheel",
      (event) => {
        event.preventDefault();
        const rect = this.svg.getBoundingClientRect();
        const x = ((event.clientX - rect.left) / rect.width) * this.width;
        const y = ((event.clientY - rect.top) / rect.height) * this.height;
        this.zoomAt(x, y, event.deltaY < 0 ? 1.2 : 1 / 1.2);
      },
      { passive: false },
    );
    this.svg.addEventListener("pointerdown", (event) => {
      if (event.button !== 0 || this.zoom <= 1) return;
      this.dragging = true;
      this.wasDragged = false;
      this.dragStartX = event.clientX;
      this.dragStartY = event.clientY;
      this.dragTranslateX = this.translateX;
      this.dragTranslateY = this.translateY;
      this.svg.setPointerCapture(event.pointerId);
      this.svg.classList.add("is-panning");
    });
    this.svg.addEventListener("pointermove", (event) => {
      if (!this.dragging) return;
      const rect = this.svg.getBoundingClientRect();
      const dx = ((event.clientX - this.dragStartX) / rect.width) * this.width;
      const dy = ((event.clientY - this.dragStartY) / rect.height) * this.height;
      this.wasDragged ||= Math.abs(dx) > 2 || Math.abs(dy) > 2;
      this.translateX = this.dragTranslateX + dx;
      this.translateY = this.dragTranslateY + dy;
      this.constrainZoom();
      this.applyZoom();
    });
    const stopDragging = (event: PointerEvent) => {
      if (!this.dragging) return;
      this.dragging = false;
      this.svg.classList.remove("is-panning");
      if (this.svg.hasPointerCapture(event.pointerId)) this.svg.releasePointerCapture(event.pointerId);
      if (this.wasDragged) window.setTimeout(() => (this.wasDragged = false), 0);
    };
    this.svg.addEventListener("pointerup", stopDragging);
    this.svg.addEventListener("pointercancel", stopDragging);
  }

  render(topo: Topo, objectName: string, opts: RenderOptions) {
    const rect = this.container.getBoundingClientRect();
    this.width = rect.width || 800;
    this.height = rect.height || 600;
    this.svg.setAttribute("viewBox", `0 0 ${this.width} ${this.height}`);
    this.svg.classList.toggle("is-interactive", Boolean(opts.onSelect));
    this.svg.classList.toggle("has-background-images", Boolean(opts.backgroundImage));
    this.svg.classList.toggle("is-landing-amphoe", Boolean(opts.landingAmphoe));
    this.svg.classList.toggle("has-regional-background", Boolean(opts.regionalBackground));
    this.svg.classList.toggle("has-relief-edges", Boolean(opts.reliefEdges));
    this.svg.replaceChildren();
    this.resetZoom();
    this.tooltip.style.display = "none"; // กัน tooltip ค้างค่าเดิมหลัง drill/re-render

    let fc = feature(topo, topo.objects[objectName]) as unknown as GeoJSON.FeatureCollection;
    if (opts.filterParent) {
      fc = {
        type: "FeatureCollection",
        features: fc.features.filter(
          (f) => String((f.properties as Record<string, unknown>)?.["parent_code"] ?? "") === opts.filterParent,
        ),
      };
    }
    if (fc.features.length === 0) return;

    const geometries = topo.objects[objectName].geometries.filter((g: Topo) => {
      const parent = String(g.properties?.parent_code ?? "");
      return !opts.filterParent || parent === opts.filterParent;
    });

    // เว้นขอบรอบแผนที่ ไม่งั้นรูปชนขอบ pane สนิท (จอแคบ เช่น iPad portrait จะดูเหมือนถูกตัด)
    const pad = Math.round(Math.max(10, Math.min(this.width, this.height) * 0.045));
    const projection: GeoProjection = geoMercator().fitExtent(
      [
        [pad, pad],
        [Math.max(pad + 1, this.width - pad), Math.max(pad + 1, this.height - pad)],
      ],
      fc,
    );
    const path = geoPath(projection);

    const colors = seqColors();
    const color =
      opts.bins && opts.bins.length
        ? scaleThreshold<number, string>().domain(opts.bins).range(colors)
        : scaleQuantile<string>().domain(Object.values(opts.values)).range(colors);
    const noData = opts.landingAmphoe
      ? cssVar("--map-landing-amphoe", cssVar("--map-no-data", "#f5f5f5"))
      : cssVar("--map-no-data", "#f5f5f5");
    const fmt = opts.formatValue ?? ((v: number) => String(v));
    const defs = document.createElementNS(SVG_NS, "defs");
    this.svg.appendChild(defs);
    const layer = document.createElementNS(SVG_NS, "g");
    layer.classList.add("map-content");
    this.svg.appendChild(layer);
    this.contentLayer = layer;
    const labels: Array<{ name: string; x: number; y: number; fontSize: number }> = [];

    for (const f of fc.features) {
      const props = f.properties as Record<string, unknown>;
      const code = String(props?.["area_code"] ?? "");
      const name = String(props?.["name_th"] ?? code);
      const v = opts.values[code];
      const backgroundImage = opts.backgroundImage?.(code);

      const el = document.createElementNS(SVG_NS, "path");
      el.setAttribute("d", path(f) ?? "");
      el.setAttribute("data-code", code);
      if (backgroundImage) {
        const [[x0, y0], [x1, y1]] = path.bounds(f);
        const width = Math.max(1, x1 - x0);
        const height = Math.max(1, y1 - y0);
        const pattern = document.createElementNS(SVG_NS, "pattern");
        const patternID = `province-background-${code}`;
        pattern.setAttribute("id", patternID);
        pattern.setAttribute("patternUnits", "userSpaceOnUse");
        pattern.setAttribute("x", String(x0));
        pattern.setAttribute("y", String(y0));
        pattern.setAttribute("width", String(width));
        pattern.setAttribute("height", String(height));
        pattern.setAttribute("viewBox", `0 0 ${width} ${height}`);

        const base = document.createElementNS(SVG_NS, "rect");
        base.setAttribute("width", String(width));
        base.setAttribute("height", String(height));
        base.setAttribute("fill", noData);
        pattern.appendChild(base);

        const image = document.createElementNS(SVG_NS, "image");
        image.setAttribute("href", backgroundImage);
        image.setAttribute("width", String(width));
        image.setAttribute("height", String(height));
        image.setAttribute("preserveAspectRatio", "xMidYMid slice");
        image.setAttribute("opacity", "1");
        pattern.appendChild(image);
        defs.appendChild(pattern);
        el.setAttribute("fill", `url(#${patternID})`);
        if (opts.backgroundImageOpacity != null) {
          el.setAttribute("fill-opacity", String(Math.min(1, Math.max(0, opts.backgroundImageOpacity))));
        }
      } else {
        el.setAttribute("fill", v == null ? noData : color(v));
      }
      el.setAttribute("aria-label", v == null ? `${name}: ไม่มีข้อมูล` : `${name}: ${fmt(v)}`);
      if (opts.selected === code) el.setAttribute("data-selected", "true");

      if (opts.onSelect) {
        const select = () => opts.onSelect?.(code);
        el.setAttribute("tabindex", "0");
        el.setAttribute("role", "button");
        el.addEventListener("click", (event) => {
          if (this.wasDragged) {
            event.preventDefault();
            event.stopPropagation();
            return;
          }
          select();
        });
        el.addEventListener("keydown", (e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            select();
          }
        });
      }
      el.addEventListener("mousemove", (e) => {
        this.tooltip.innerHTML = `<strong>${name}</strong><br>${v == null ? "ไม่มีข้อมูล" : fmt(v)}`;
        this.tooltip.style.display = "block";
        const r = this.container.getBoundingClientRect();
        this.tooltip.style.left = `${e.clientX - r.left + 12}px`;
        this.tooltip.style.top = `${e.clientY - r.top + 12}px`;
      });
      el.addEventListener("mouseleave", () => {
        this.tooltip.style.display = "none";
      });
      layer.appendChild(el);

      if (opts.showAreaLabels) {
        const [[x0, y0], [x1, y1]] = path.bounds(f);
        const [x, y] = path.centroid(f);
        const width = x1 - x0;
        const height = y1 - y0;
        // ชื่อไทยเฉลี่ยกว้างราว .76em; ซ่อน label ที่เล็กเกินไปแทนการทับกัน
        const fontSize = Math.min(13, width / Math.max(name.length * 0.76, 1), height * 0.38);
        if (Number.isFinite(x) && Number.isFinite(y) && fontSize >= 7) {
          labels.push({ name, x, y, fontSize });
        }
      }
    }

    // รวม polygon เพื่อให้เหลือเฉพาะเส้นรอบนอกของประเทศ
    // (ไม่ทับเส้นแบ่งเขตภายในที่วาดจากแต่ละพื้นที่ด้านบน)
    if (opts.showOuterBoundary && geometries.length) {
      const boundary = document.createElementNS(SVG_NS, "path");
      boundary.classList.add("map-outer-boundary");
      boundary.setAttribute("d", path(merge(topo, geometries)) ?? "");
      boundary.setAttribute("fill", "none");
      boundary.setAttribute("aria-hidden", "true");
      layer.appendChild(boundary);
    }

    if (opts.showAreaLabels) {
      const labelLayer = document.createElementNS(SVG_NS, "g");
      labelLayer.classList.add("map-labels");
      for (const label of labels) {
        const text = document.createElementNS(SVG_NS, "text");
        text.setAttribute("x", String(label.x));
        text.setAttribute("y", String(label.y));
        text.setAttribute("text-anchor", "middle");
        text.setAttribute("dominant-baseline", "central");
        text.setAttribute("font-size", `${label.fontSize.toFixed(1)}px`);
        text.textContent = label.name;
        labelLayer.appendChild(text);
      }
      layer.appendChild(labelLayer);
    }
  }

  // highlight ทำเครื่องหมายพื้นที่ที่เลือก โดยไม่ต้อง re-render ทั้งแผนที่
  highlight(code: string | null) {
    this.svg.querySelectorAll("path[data-selected]").forEach((el) => el.removeAttribute("data-selected"));
    if (code) {
      this.svg.querySelector(`path[data-code="${code}"]`)?.setAttribute("data-selected", "true");
    }
  }

  clear() {
    this.svg.replaceChildren();
    this.contentLayer = null;
    this.resetZoom();
    this.tooltip.style.display = "none";
  }

  private zoomAt(x: number, y: number, factor: number) {
    if (!this.contentLayer || !this.width || !this.height) return;
    const next = Math.min(6, Math.max(1, this.zoom * factor));
    if (next === this.zoom) return;
    const ratio = next / this.zoom;
    this.translateX = x - ratio * (x - this.translateX);
    this.translateY = y - ratio * (y - this.translateY);
    this.zoom = next;
    this.constrainZoom();
    this.applyZoom();
  }

  private resetZoom() {
    this.zoom = 1;
    this.translateX = 0;
    this.translateY = 0;
    this.applyZoom();
  }

  private constrainZoom() {
    this.translateX = Math.min(0, Math.max(this.width * (1 - this.zoom), this.translateX));
    this.translateY = Math.min(0, Math.max(this.height * (1 - this.zoom), this.translateY));
  }

  private applyZoom() {
    this.contentLayer?.setAttribute("transform", `translate(${this.translateX} ${this.translateY}) scale(${this.zoom})`);
    this.svg.classList.toggle("is-zoomed", this.zoom > 1);
  }
}
